// V17.262: AR Residential daily team board API client.
//
// Endpoints:
//   GET    /api/v1/ar/residential/team-board                     → human auth
//   GET    /api/v1/ar/residential/team-board/display             → X-Display-Token
//   POST   /api/v1/ar/residential/team-board/display-tokens      → pair a TV
//   GET    /api/v1/ar/residential/team-board/display-tokens      → list
//   DELETE /api/v1/ar/residential/team-board/display-tokens/:id  → revoke

import { apiClient } from './api';

const BASE = '/ar/residential/team-board';

export interface TeamBoardRow {
  no: number;
  officer_id: string;
  officer_name: string;
  team: string | null;
  call_target: number | null;
  calls_actual: number;
  daily_target: number | null;
  daily_target_derived: boolean;
  collected_today: number;
  month_target: number | null;
  mtd_actual: number;
  call_progress: number | null;
  collection_progress: number | null;
  mtd_progress: number | null;
  // V17.289: gap to target, clamped at 0; null when the row has no target.
  calls_remaining: number | null;
  collection_remaining: number | null;
  mtd_remaining: number | null;
}

export interface TeamBoardTotals {
  call_target: number;
  calls_actual: number;
  daily_target: number;
  collected_today: number;
  month_target: number;
  mtd_actual: number;
  call_progress: number | null;
  collection_progress: number | null;
  mtd_progress: number | null;
  calls_remaining: number;
  collection_remaining: number;
  mtd_remaining: number;
  officer_count: number;
}

export interface TeamBoardGroup {
  team: string;
  rows: TeamBoardRow[];
  total: TeamBoardTotals;
}

/**
 * V17.299: which inputs have actually recorded anything in the last 90 days.
 *
 * A false flag means the dependent columns are UNINSTRUMENTED — the client
 * must render an em dash, not a 0. "Nothing was recorded" and "nobody
 * collected" are different claims and the board must not conflate them.
 */
export interface TeamBoardSources {
  collections_recorded_90d: boolean;
  calls_recorded_90d: boolean;
  ledger_recorded_90d: boolean;
}

export interface TeamBoard {
  as_of: string;
  generated_at: string;
  teams: TeamBoardGroup[];
  grand_total: TeamBoardTotals;
  /** Optional: the residential board does not send it, and treats all sources
   *  as instrumented. Only the commercial board populates it today. */
  sources?: TeamBoardSources;
  /** V17.359, commercial only: when the Legacy billings delta sync last ran
   *  (ISO, ICT offset). null until the first board view triggers it. */
  billing_last_synced_at?: string | null;
}

export interface DisplayToken {
  id: string;
  label: string;
  scope: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export interface DisplayTokenIssued {
  token: string;
  url_path: string;
  display_token: DisplayToken;
}

export async function fetchTeamBoard(): Promise<TeamBoard> {
  return apiClient.get<TeamBoard>(BASE);
}

/** V17.292: today's board as an .xlsx blob. Role-gated, so it must ride the
 * authenticated apiClient — a plain link/window.open would 401. */
export async function exportTeamBoardXlsx(): Promise<Blob> {
  const response = await apiClient.get<{ data: Blob }>(
    `${BASE}/export`,
    undefined,
    { responseType: 'blob' },
  );
  return response.data;
}

/** Paired-display read. The token never rides in the query string after pairing. */
export async function fetchTeamBoardForDisplay(token: string): Promise<TeamBoard> {
  return apiClient.get<TeamBoard>(`${BASE}/display`, undefined, {
    headers: { 'X-Display-Token': token },
  });
}

export async function createDisplayToken(label: string): Promise<DisplayTokenIssued> {
  return apiClient.post<DisplayTokenIssued>(`${BASE}/display-tokens`, { label });
}

export async function listDisplayTokens(): Promise<DisplayToken[]> {
  return apiClient.get<DisplayToken[]>(`${BASE}/display-tokens`);
}

export async function revokeDisplayToken(id: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/display-tokens/${id}`);
}
